import type { CurriculumPathway, Learner, SubjectBlocks as Blocks } from "../types";

/**
 * What a curriculum pathway actually promises, as data.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Five pathways are offered, chosen and charged for, and until now the choice
 * changed nothing a family could see. The enrolment screen described each one in
 * prose, the curriculum screen never mentioned the pathway at all, and the
 * activity library was filtered by the child's level alone. A family paying for
 * Cambridge Bilingual and a family on the Pakistan Curriculum were shown exactly
 * the same thing.
 *
 * The allocations here are the ones quoted to families on the public site and in
 * the enrolment chooser, so this file is the single place they are written down
 * as numbers rather than as sentences. Each pathway totals 20 weekly blocks.
 */

export type SubjectBlocks = { subject: string; blocks: number };

/**
 * Balanced is the only pathway whose published description gives no numbers —
 * it pairs its subjects ("English and Urdu", "Mathematics and science",
 * "Islamiyat and social studies") rather than weighting them. An even split
 * across those six is what "balanced" says, so that is what it gets.
 */
export const PATHWAY_BLOCKS: Record<CurriculumPathway, SubjectBlocks[]> = {
  balanced: [
    { subject: "English", blocks: 4 },
    { subject: "Urdu", blocks: 4 },
    { subject: "Mathematics", blocks: 4 },
    { subject: "Science", blocks: 4 },
    { subject: "Islamiyat", blocks: 2 },
    { subject: "Social Studies", blocks: 2 },
  ],
  pakistan: [
    { subject: "Urdu", blocks: 4 },
    { subject: "English", blocks: 4 },
    { subject: "Mathematics", blocks: 5 },
    { subject: "Science", blocks: 3 },
    { subject: "Islamiyat", blocks: 2 },
    { subject: "Social Studies", blocks: 2 },
  ],
  cambridge: [
    { subject: "English", blocks: 5 },
    { subject: "Mathematics", blocks: 5 },
    { subject: "Science", blocks: 4 },
    { subject: "Urdu", blocks: 2 },
    { subject: "Global Perspectives", blocks: 2 },
    { subject: "Digital Literacy", blocks: 2 },
  ],
  faith: [
    { subject: "English", blocks: 4 },
    { subject: "Mathematics", blocks: 5 },
    { subject: "Urdu", blocks: 3 },
    { subject: "Science", blocks: 3 },
    { subject: "Quran and Arabic", blocks: 3 },
    { subject: "Islamiyat and Character", blocks: 2 },
  ],
  future_skills: [
    { subject: "English", blocks: 4 },
    { subject: "Mathematics", blocks: 5 },
    { subject: "STEAM", blocks: 4 },
    { subject: "Urdu", blocks: 2 },
    { subject: "Digital Literacy", blocks: 2 },
    { subject: "Global and Financial Literacy", blocks: 2 },
    { subject: "Character", blocks: 1 },
  ],
};

export const WEEKLY_BLOCK_TOTAL = 20;

/**
 * The allocation actually in force for one child.
 *
 * A pathway supplies the starting split and a family may then move blocks
 * between subjects. Reading it through here rather than off the learner means
 * the editor, the curriculum screen and the matching console cannot drift into
 * three different opinions about what this child's week looks like.
 */
export function blocksFor(
  learner: Pick<Learner, "curriculum_pathway" | "pathway_blocks"> | null,
): Blocks[] {
  if (!learner) return [];
  const custom = learner.pathway_blocks;
  // A stored allocation wins, but only if it is actually usable — a row that
  // somehow holds an empty array should fall back to the pathway rather than
  // render a child with no week at all.
  if (Array.isArray(custom) && custom.length > 0) {
    return custom.filter((line) => line && typeof line.subject === "string" && line.blocks > 0);
  }
  const pathway = learner.curriculum_pathway;
  return pathway && PATHWAY_BLOCKS[pathway] ? PATHWAY_BLOCKS[pathway] : PATHWAY_BLOCKS.balanced;
}

/** Move one block onto a subject, taking it from the largest other subject. */
export function addBlock(lines: Blocks[], subject: string): Blocks[] {
  const total = lines.reduce((sum, line) => sum + line.blocks, 0);
  if (total >= WEEKLY_BLOCK_TOTAL) {
    // The week is full, so a block has to come from somewhere. Taking it from
    // the largest subject is the least surprising choice and keeps the total
    // pinned, which is what stops a family building a 26-block week nobody can
    // timetable.
    const donor = lines
      .filter((line) => line.subject !== subject && line.blocks > 1)
      .sort((a, b) => b.blocks - a.blocks)[0];
    if (!donor) return lines;
    return lines.map((line) =>
      line.subject === donor.subject
        ? { ...line, blocks: line.blocks - 1 }
        : line.subject === subject
          ? { ...line, blocks: line.blocks + 1 }
          : line,
    );
  }
  return lines.map((line) => (line.subject === subject ? { ...line, blocks: line.blocks + 1 } : line));
}

/** Take a block off a subject. Never below one: zero would be a removal. */
export function removeBlock(lines: Blocks[], subject: string): Blocks[] {
  return lines.map((line) =>
    line.subject === subject && line.blocks > 1 ? { ...line, blocks: line.blocks - 1 } : line,
  );
}

export function blockTotal(lines: Blocks[]): number {
  return lines.reduce((sum, line) => sum + line.blocks, 0);
}

/**
 * Which library areas satisfy a pathway subject — and it depends on the stage.
 *
 * The activity library is written in DEVELOPMENTAL areas, not school subjects:
 * a five-year-old does "Early mathematics" and "The world around us", not
 * "Mathematics" and "Science". That is the right way round for the youngest
 * children and it is why the two vocabularies do not simply line up. Mapping
 * them here keeps the pathway honest about what a family will actually see
 * without renaming a single activity.
 *
 * Foundation covers playgroup, nursery and kindergarten; primary covers Class
 * 1-5. A subject with no areas listed for a stage genuinely has no content at
 * that stage, and the curriculum screen says so rather than showing a silent
 * zero.
 */
const AREA_MAP: Record<string, { foundation: string[]; primary: string[] }> = {
  English: {
    foundation: ["Early language", "Reading", "Writing"],
    primary: ["English", "Reading", "Writing"],
  },
  Urdu: { foundation: ["Urdu"], primary: ["Urdu"] },
  Mathematics: { foundation: ["Early mathematics"], primary: ["Mathematics"] },
  Science: { foundation: ["The world around us"], primary: ["Science", "The world around us"] },
  Islamiyat: { foundation: ["Faith and character"], primary: ["Faith and character"] },
  "Social Studies": {
    foundation: ["The world around us", "Growing up together", "Social confidence"],
    primary: ["The world around us", "Social confidence"],
  },
  "Quran and Arabic": { foundation: ["Faith and character"], primary: ["Faith and character"] },
  "Islamiyat and Character": {
    foundation: ["Faith and character", "Independence"],
    primary: ["Faith and character", "Independence"],
  },
  Character: {
    foundation: ["Independence", "Wellbeing", "Social confidence"],
    primary: ["Independence", "Wellbeing", "Social confidence"],
  },
  "Global Perspectives": {
    foundation: ["The world around us", "Growing up together"],
    primary: ["The world around us", "Social confidence"],
  },
  "Digital Literacy": {
    foundation: ["Computers and thinking machines"],
    primary: ["Computers and thinking machines"],
  },
  STEAM: {
    foundation: ["Thinking skills", "Creative expression", "Early mathematics"],
    primary: ["Science", "Thinking skills", "Creative expression"],
  },
  "Global and Financial Literacy": {
    foundation: ["Money sense", "Life skills"],
    primary: ["Money sense", "Life skills"],
  },
};

/** The library areas that count towards one pathway subject at one stage. */
export function areasForSubject(subject: string, stage: string): string[] {
  const entry = AREA_MAP[subject];
  if (!entry) return [];
  return stage === "primary" || stage === "lower-secondary" ? entry.primary : entry.foundation;
}
